import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// M3X.COMP F.5 — comp changes list. Filterable by workflow_state.
export function CompChangesPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const q = useQuery({
    queryKey: ["comp-changes-list", legalEntityId],
    queryFn: () =>
      api.listCompChanges({ legal_entity_id: legalEntityId! }),
    enabled: !!legalEntityId,
  });

  if (gate) return gate;
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel message={(q.error as Error).message} />;

  const changes = q.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("comp_changes.title")}
        subtitle={t("comp_changes.subtitle")}
        actions={
          <Link
            to="/operations/transactions/comp/new"
            className="inline-flex items-center gap-1 rounded bg-brand-deep px-3 py-1.5 text-sm text-white hover:bg-slate-900"
          >
            <Plus className="h-4 w-4" />
            {t("comp_changes.new")}
          </Link>
        }
      />

      {changes.length === 0 ? (
        <EmptyState
          headline={t("comp_changes.empty_title")}
          description={t("comp_changes.empty_desc")}
        />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th>#</th>
              <th>{t("comp_changes.col_type")}</th>
              <th>{t("comp_changes.col_effective")}</th>
              <th>{t("comp_changes.col_delta")}</th>
              <th>{t("comp_changes.col_state")}</th>
              <th>{t("comp_changes.col_article_60")}</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2">
                  <Link
                    to={`/operations/transactions/comp/${c.id}`}
                    className="text-brand-deep hover:underline"
                  >
                    CC-{String(c.change_number ?? 0).padStart(6, "0")}
                  </Link>
                </td>
                <td>{c.change_type}</td>
                <td>{c.effective_date}</td>
                <td>
                  {c.delta_pct !== null
                    ? `${c.delta_pct}%`
                    : "—"}
                </td>
                <td>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                    {c.workflow_state}
                  </span>
                </td>
                <td>
                  {c.article_60_required ? (
                    c.article_60_consent_signed ? (
                      <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                        {t("comp_changes.consent_signed")}
                      </span>
                    ) : (
                      <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">
                        {t("comp_changes.consent_pending")}
                      </span>
                    )
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
